import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, Menu, ShieldCheck, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { NAV } from "@/constants/testIds";

export default function Header() {
  const { user, logout } = useAuth();
  const { totals, setDrawerOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const nav = useNavigate();

  const linkCls =
    "text-sm font-medium text-slate-700 hover:text-emerald-900 transition-colors duration-200";

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 group">
          <div className="w-9 h-9 bg-emerald-900 flex items-center justify-center rounded-sm">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display font-black text-lg tracking-tight text-slate-900">
              MedSupply
            </span>
            <span className="overline text-emerald-800 mt-0.5">Pro Distribution</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/shop" data-testid={NAV.shop} className={linkCls}>
            Shop
          </Link>
          <Link to="/categories" data-testid={NAV.categories} className={linkCls}>
            Categories
          </Link>
          <Link to="/contact" data-testid={NAV.contact} className={linkCls}>
            Contact
          </Link>
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid={NAV.adminLink}
              className="text-sm font-medium text-emerald-900 hover:text-emerald-700"
            >
              Admin
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-1">
          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/account"
                data-testid={NAV.account}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:text-emerald-900"
              >
                <User className="w-4 h-4" />
                <span className="hidden lg:inline">{user.name.split(" ")[0]}</span>
              </Link>
              <button
                data-testid={NAV.logoutBtn}
                onClick={() => {
                  logout();
                  nav("/");
                }}
                className="flex items-center gap-1 px-3 py-2 text-sm text-slate-500 hover:text-slate-900"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid={NAV.loginLink}
              className="hidden md:flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:text-emerald-900"
            >
              <User className="w-4 h-4" />
              Sign in
            </Link>
          )}

          <button
            data-testid={NAV.cartButton}
            onClick={() => setDrawerOpen(true)}
            className="relative flex items-center gap-2 px-4 py-2.5 bg-emerald-900 text-white text-sm font-medium rounded-sm hover:bg-emerald-800 transition-colors"
          >
            <ShoppingBag className="w-4 h-4" />
            <span className="hidden sm:inline">Cart</span>
            {totals.count > 0 && (
              <span
                data-testid={NAV.cartCount}
                className="ml-1 min-w-[20px] h-5 flex items-center justify-center bg-white text-emerald-900 text-xs font-bold rounded-full px-1.5"
              >
                {totals.count}
              </span>
            )}
          </button>

          <button
            className="md:hidden p-2 text-slate-700"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white px-6 py-4 flex flex-col gap-3">
          <Link to="/shop" onClick={() => setMenuOpen(false)} className={linkCls}>Shop</Link>
          <Link to="/categories" onClick={() => setMenuOpen(false)} className={linkCls}>Categories</Link>
          <Link to="/contact" onClick={() => setMenuOpen(false)} className={linkCls}>Contact</Link>
          {user ? (
            <>
              <Link to="/account" onClick={() => setMenuOpen(false)} className={linkCls}>Account</Link>
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setMenuOpen(false)} className="text-sm font-medium text-emerald-900">Admin</Link>
              )}
              <button
                onClick={() => { logout(); setMenuOpen(false); nav("/"); }}
                className="text-left text-sm text-slate-500"
              >
                Sign out
              </button>
            </>
          ) : (
            <Link to="/login" onClick={() => setMenuOpen(false)} className={linkCls}>Sign in</Link>
          )}
        </div>
      )}
    </header>
  );
}
